'use client'

import { motion, useMotionValue, useTransform } from 'framer-motion'
import { useState } from 'react'

interface CardRotateProps {
  children: React.ReactNode
  onSendToBack: () => void
  onCardClick?: () => void
  sensitivity: number
}

function CardRotate({
  children,
  onSendToBack,
  onCardClick,
  sensitivity,
}: CardRotateProps) {
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const rotateX = useTransform(y, [-100, 100], [60, -60])
  const rotateY = useTransform(x, [-100, 100], [-60, 60])
  const [isDragging, setIsDragging] = useState(false)

  function handleDragEnd(_: never, info: { offset: { x: number; y: number } }) {
    setIsDragging(false)
    if (
      Math.abs(info.offset.x) > sensitivity ||
      Math.abs(info.offset.y) > sensitivity
    ) {
      onSendToBack()
    } else {
      x.set(0)
      y.set(0)
    }
  }

  function handleClick() {
    if (!isDragging) {
      onCardClick?.()
    }
  }

  return (
    <motion.div
      className="absolute cursor-grab"
      style={{ x, y, rotateX, rotateY }}
      drag
      dragConstraints={{ top: 0, right: 0, bottom: 0, left: 0 }}
      dragElastic={0.6}
      whileTap={{ cursor: 'grabbing' }}
      onDragEnd={handleDragEnd}
      onDragStart={() => setIsDragging(true)}
      onClick={handleClick}
    >
      {children}
    </motion.div>
  )
}

interface StackProps {
  randomRotation?: boolean
  sensitivity?: number
  cardDimensions?: { width: number; height: number }
  sendToBackOnClick?: boolean
  cardsData?: {
    id: string
    content: React.ReactNode
    onCardClick?: () => void
  }[]
  animationConfig?: { stiffness: number; damping: number }
  showIndicators?: boolean
}

export default function Stack({
  randomRotation = false,
  sensitivity = 100,
  cardDimensions = { width: 208, height: 208 },
  cardsData = [],
  animationConfig = { stiffness: 260, damping: 20 },
  sendToBackOnClick = false,
  showIndicators = false,
}: StackProps) {
  const [cards, setCards] = useState(cardsData.length ? cardsData : [])
  const [currentPosition, setCurrentPosition] = useState(0)

  const sendToBack = (id: string) => {
    setCards((prev) => {
      const newCards = [...prev]
      const index = newCards.findIndex((card) => card.id === id)
      const [card] = newCards.splice(index, 1)
      newCards.unshift(card)

      const newPosition = (currentPosition + 1) % cards.length
      setCurrentPosition(newPosition)
      return newCards
    })
  }

  return (
    <div
      className="relative"
      style={{
        width: cardDimensions.width,
        height: cardDimensions.height,
        perspective: 600,
      }}
    >
      {cards.map((card, index) => {
        const randomRotate = randomRotation
          ? Math.random() * 10 - 5 // Random degree between -5 and 5
          : 0

        return (
          <CardRotate
            key={card.id}
            onSendToBack={() => sendToBack(card.id)}
            onCardClick={card.onCardClick}
            sensitivity={sensitivity}
          >
            <motion.div
              className="overflow-hidden rounded-[36px]"
              onClick={() => sendToBackOnClick && sendToBack(card.id)}
              animate={{
                rotateZ: (cards.length - index - 1) * 4 + randomRotate,
                scale: 1 + index * 0.06 - cards.length * 0.06,
                transformOrigin: '90% 90%',
              }}
              initial={false}
              transition={{
                type: 'spring',
                stiffness: animationConfig.stiffness,
                damping: animationConfig.damping,
              }}
              style={{
                width: cardDimensions.width,
                height: cardDimensions.height,
              }}
            >
              {card.content}
            </motion.div>
          </CardRotate>
        )
      })}
      {showIndicators && (
        <div className="absolute bottom-5 right-6 z-50 flex gap-1">
          {cards.map((_, index) => (
            <div
              key={index}
              style={{
                filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))',
              }}
              className={`h-1.5 w-1.5 rounded-full ${
                index === currentPosition ? 'bg-white' : 'bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
